import * as React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Trash2Icon } from 'lucide-react';
import { useBlockedProductTaskTypeOptions } from 'hooks/api/options';
import { Form, FormField } from 'components/ui/form';
import { ComboboxGs, InputGs } from 'components/ui/Forms';
import { Button } from 'components/ui/ButtonGs';
import { Table, TableHead, TableBody, TableCell, TableRow, TableHeader } from 'components/ui/table';
import { cn } from 'lib/utils';
import { StringUtils } from 'utils';

import { formSchema, type FormSchema } from './form-schema';

type Task = {
  id: string;
  name: string;
};

export type TasksSelectorRef = {
  getData: () => string[];
  reset: () => void;
}

type Props = React.HTMLAttributes<HTMLDivElement>;

const CUSTOM_TASK_OPTION_VALUE = StringUtils.generateUuid();
const CUSTOM_TASK_OPTION_NAME = 'Outro';

export const TasksSelector = React.forwardRef<TasksSelectorRef, Props>((props, ref) => {
  const [tasks, setTasks] = React.useState<Task[]>([]);
  const [showCustomTask, setShowCustomTask] = React.useState(false);

  const _taskOptions = useBlockedProductTaskTypeOptions();

  const taskOptions = React.useMemo(() => {
    const selectedIds = tasks.map(t => t.id);
    const options = _taskOptions.singleSelectOptions.map(option => {
      return {
        ...option,
        disabled: selectedIds.includes(option.value),
      };
    });

    const customTaskOption = {
      name: CUSTOM_TASK_OPTION_NAME,
      value: CUSTOM_TASK_OPTION_VALUE,
      disabled: false,
    };

    return [...options, customTaskOption];
  }, [_taskOptions.singleSelectOptions, tasks]);

  const form = useForm<FormSchema>({
    resolver: zodResolver(formSchema),
  });

  React.useImperativeHandle(ref, () => ({
    getData: () => tasks.map(t => t.name),
    reset: () => {
      form.reset();
      setTasks([]);
    },
  }), [tasks, form]);

  const addTaskToList = React.useCallback((optionValue: string) => {
    const selectedTask = taskOptions
      .find(o => o.value === optionValue);

    if (!selectedTask) return;

    const newTask = {
      id: selectedTask.value,
      name: selectedTask.name,
    };

    setTasks(tasks => [...tasks, newTask]);
  }, [taskOptions]);

  const removeTaskFromList = React.useCallback((taskId: string) => {
    setTasks(tasks => tasks.filter(task => task.id !== taskId));
  }, []);

  const onTaskOptionChange = React.useCallback((value?: string) => {
    form.resetField('customTask');
    setShowCustomTask(value === CUSTOM_TASK_OPTION_VALUE);
  }, [form]);

  const onFormSubmit = React.useCallback((formData: FormSchema) => {
    form.clearErrors('customTask');

    if (!formData.task) return;

    const customTaskSelected = formData.task === CUSTOM_TASK_OPTION_VALUE;
    const customTaskText = formData.customTask;

    if (customTaskSelected && !customTaskText) {
      form.setError('customTask', {
        message: 'Descreva o tipo de trabalho',
      });
    } else if (customTaskSelected && customTaskText) {
      const newTask = {
        id: StringUtils.generateUuid(),
        name: customTaskText,
      };
      setTasks(tasks => [...tasks, newTask]);
      setShowCustomTask(false);
      form.reset();
    } else {
      addTaskToList(formData.task);
      form.reset();
    }

  }, [addTaskToList, form]);

  return (
    <div {...props}>
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onFormSubmit)}
          className={cn('grid gap-4 mb-4 grid-cols-2', showCustomTask && 'grid-cols-3')}
        >
          <FormField
            control={form.control}
            name={'task'}
            render={({ field }) => (
              <ComboboxGs
                form={form}
                fieldName={field.name}
                fieldValue={field.value}
                content={taskOptions}
                label={'Tipo de trabalho'}
                clearable
                portal={false}
                onChange={onTaskOptionChange}
                onClear={onTaskOptionChange}
              />
            )}
          />
          {showCustomTask && (
            <FormField
              control={form.control}
              name={'customTask'}
              render={({ field }) => (
                <InputGs {...field} type="text" label={'Descrição'} />
              )}
            />
          )}
          <Button.Root
            type={'submit'}
            size={'sm'}
            disabled={!taskOptions.some(o => !o?.disabled)}
            className={'mt-[26px]'}
          >
            Adicionar trabalho
          </Button.Root>
        </form>
      </Form>
      {tasks.length > 0 && (
        <div className={'bg-gray-50 p-4'}>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Tipo de trabalho</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {tasks.map(task => (
                <TableRow key={task.id}>
                  <TableCell>{task.name}</TableCell>
                  <TableCell className={'text-right'}>
                    <button
                      onClick={() => removeTaskFromList(task.id)}
                      className={'text-red-500'}
                    >
                      <Trash2Icon />
                    </button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
});
TasksSelector.displayName = 'TasksSelector';